import React, { FC, ReactElement } from 'react'
import { message, Spin, Typography, Card, Space, Row } from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiCall } from '../../../api/apiCall'
import { FileUploader } from '../../Shared/FileUploader/FileUploader'
import { AttachmentRecord } from '../../Shared/FileUploader/types'
import { DocumentUploadProps } from './types'
import { Requests, ReactQuery } from 'api'
import { MESSAGES } from './constants'
import styles from './styles.module.scss'

export const DocumentUpload: FC<DocumentUploadProps> = ({ orderId, currentUserId }): ReactElement => {
  const queryClient = useQueryClient()
  const attachmentsRequest = Requests.attachments({ orderId })
  const { data: attachmentsData, isLoading } = useQuery({
    queryKey: [attachmentsRequest.type, orderId],
    queryFn: () => ReactQuery.fetchResources(attachmentsRequest)
  })

  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: number) => {
      await apiCall.delete(Requests.attachment({ id: attachmentId, orderId }).path)
      return attachmentId
    },
    onSuccess: (attachmentId) => {
      queryClient.setQueryData(
        [attachmentsRequest.type, orderId],
        (prev: { resources: AttachmentRecord[] } | undefined) => ({
          ...prev,
          resources: (prev?.resources ?? []).filter((a) => a.id !== attachmentId)
        })
      )
    },
    onError: () => {
      message.error(MESSAGES.DELETE_ERROR)
    }
  })

  const handleDelete = async (attachment: AttachmentRecord) => {
    await deleteMutation.mutateAsync(attachment.id)
  }

  const handleDownload = async (attachment: AttachmentRecord) => {
    try {
      const response = await apiCall.get(Requests.attachment({ id: attachment.id, orderId }).path)
      window.open(response.data.url, '_blank')
    } catch {
      message.error(MESSAGES.DOWNLOAD_ERROR)
    }
  }

  const handleUploadComplete = (newAttachment: AttachmentRecord) => {
    queryClient.setQueryData(
      [attachmentsRequest.type, orderId],
      (prev: { resources: AttachmentRecord[] } | undefined) => ({
        ...prev,
        resources: [...(prev?.resources ?? []), newAttachment]
      })
    )
  }

  return (
    <Card>
      <Space>
        <Typography.Text type="secondary">
          <FileTextOutlined className={styles.headerIcon} />
        </Typography.Text>
        <Typography.Title level={4}>{MESSAGES.UPLOAD_DOCUMENT_TITLE}</Typography.Title>
      </Space>

      {isLoading ? (
        <Row justify="center">
          <Spin />
        </Row>
      ) : (
        <FileUploader
          presignedPostPath={Requests.attachmentPresignedPost({ orderId }).path}
          confirmPath={Requests.attachments({ orderId }).path}
          multiple={true}
          fileList={attachmentsData?.resources}
          currentUserId={currentUserId}
          onUploadComplete={handleUploadComplete}
          onDownload={handleDownload}
          onRemove={handleDelete}
        />
      )}
    </Card>
  )
}

export default DocumentUpload
